import { LoggerService } from '@angular-ru/cdk/logger';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ICoreContainer } from 'src/app/api/modules/core/dynamic/ICoreContainer';
import { DynamicContentDirective } from 'src/app/modules/shared/directives/dynamic-content/dynamic-content.directive';
import { flexContainerStyle } from 'src/app/util/FlexLayoutTokens';
import { ContainerFactoryArray } from './container.factory';

/**
 * The flex direction applied when the configuration does not provide a `layout`.
 */
const DEFAULT_LAYOUT = 'column';

/**
 * The alignment applied when the configuration does not provide a `layoutAlign`.
 */
const DEFAULT_LAYOUT_ALIGN = 'space-between center';

/**
 * Renders a server-configured array of dynamic components inside a flexbox
 * wrapper. The configured `layout` / `layoutAlign` tokens are translated to
 * plain CSS flex styles, and each entry of `componentArray` is created
 * dynamically by selector.
 *
 * Nothing renders until a truthy config with a non-empty `componentArray`
 * arrives; when the config changes, the previously created children are
 * destroyed before the new ones are created.
 *
 * See {@link ICoreContainer} for the configuration structure.
 */
@Component({
  selector: 'app-container',
  templateUrl: './container.component.html',
  styleUrl: './container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DynamicContentDirective],
})
export class ContainerComponent {
  private readonly loggerService = inject(LoggerService);

  /**
   * The anchor the child components are dynamically created into.
   */
  private readonly contentContainer = viewChild(DynamicContentDirective);

  /**
   * The Config as provided to the element.
   *
   * Consumers may bind `null` / `undefined` sources (e.g. `| async`); the
   * container simply renders nothing until a usable config arrives.
   */
  readonly config = input<ICoreContainer | null>();

  /**
   * The validated child component configurations, or a warning when the config
   * can't be rendered (logged by an effect; the container then renders nothing).
   */
  private readonly viewConfig = computed(() => {
    const configuration = this.config();
    if (!configuration) {
      return undefined;
    }

    const componentArray = configuration.componentArray ?? [];
    if (componentArray.length === 0) {
      return { components: undefined, warning: 'Containers must be configured with a non-empty "componentArray".' };
    }

    return { components: componentArray, warning: undefined };
  });

  /**
   * What the wrapper element binds: the flexbox styles translated from the
   * configured layout tokens, and the optional aria-label. `undefined` while
   * there is nothing to render.
   */
  protected readonly templateData = computed(() => {
    const configuration = this.config();
    if (!configuration || !this.viewConfig()?.components) {
      return undefined;
    }

    return {
      ariaLabel: configuration.ariaLabel || null,
      style: flexContainerStyle(configuration.layout || DEFAULT_LAYOUT, configuration.layoutAlign || DEFAULT_LAYOUT_ALIGN),
    };
  });

  /**
   * References to the dynamically created child components, for teardown.
   */
  private readonly componentRefs = signal<ComponentRef<unknown>[]>([]);

  constructor() {
    // surface configuration problems (once per config change)
    effect(() => {
      const warning = this.viewConfig()?.warning;
      if (warning) {
        this.loggerService.warn(`[Container] - ${warning}`);
      }
    });

    // (re)create the children whenever the anchor or the configs change
    effect(() => {
      const container = this.contentContainer();
      const components = this.viewConfig()?.components;

      untracked(() => this.componentRefs()).forEach((ref) => ref.destroy());
      this.componentRefs.set(container && components ? ContainerFactoryArray(container.viewContainerRef, components) : []);
    });

    inject(DestroyRef).onDestroy(() => {
      this.componentRefs().forEach((ref) => ref.destroy());
    });
  }
}
